import { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    CircularProgress,
    Alert,
    AlertTitle,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { socket } from "../../botSocket";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";

const EnterOTPModal = ({
    enterOTPOpen,
    setEnterOTPOpen,
    setLoginWasNotCompletedOpen,
}) => {
    const { userId } = useAuth();
    const { selectedProject } = useProject();

    const [enterOTPLoading, setEnterOTPLoading] = useState(false);
    const [otp, setOTP] = useState("");
    const [otpError, setOTPError] = useState("");
    const [timeLeft, setTimeLeft] = useState(90); // 1.5 minutes = 90 seconds
    const skipTimeoutOnOpenRef = useRef(false);

    // Reset modal state when it opens.
    useEffect(() => {
        if (enterOTPOpen) {
            skipTimeoutOnOpenRef.current = true;
            setTimeLeft(90);
            setOTP("");
            setOTPError("");
        } else {
            skipTimeoutOnOpenRef.current = false;
        }
    }, [enterOTPOpen]);

    // Timer effect
    useEffect(() => {
        let interval = null;

        if (enterOTPOpen && timeLeft > 0) {
            skipTimeoutOnOpenRef.current = false;
            interval = setInterval(() => {
                setTimeLeft((prevTimeLeft) => prevTimeLeft - 1);
            }, 1000);
        } else if (timeLeft === 0 && enterOTPOpen) {
            // Ignore stale 0 on the same render cycle as modal open.
            if (skipTimeoutOnOpenRef.current) {
                return undefined;
            }

            // Auto-submit empty string when timer expires
            socket.emit("code-response", { userId, code: "" });
            setEnterOTPOpen(false);
            setOTP("");
            setOTPError("");
            setLoginWasNotCompletedOpen(true);
        }

        return () => clearInterval(interval);
    }, [
        enterOTPOpen,
        timeLeft,
        userId,
        setEnterOTPOpen,
        setLoginWasNotCompletedOpen,
    ]);

    const handleClose = () => {
        setEnterOTPOpen(false);
        setOTP("");
        setOTPError("");
        setTimeLeft(90);
        setLoginWasNotCompletedOpen(true);
        socket.emit("code-response", { userId, code: "" });
    };

    const handleOTPChange = (e) => {
        setOTP(e.target.value.trim());
        if (otpError) {
            setOTPError("");
        }
    };

    const handleEnterOTP = () => {
        if (!otp.trim()) {
            setOTPError("OTP is required.");
            return;
        }

        socket.emit("code-response", { userId, code: otp });

        setEnterOTPLoading(false);
        setEnterOTPOpen(false);
        setTimeLeft(90);
        // Need to check result here. If result failed -> need to clear state and open "Was not completed" modal:
        // setOTP('')
        // setOTPError('')
        // setLoginWasNotCompletedOpen(true)
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    };

    return (
        <Dialog open={enterOTPOpen} onClose={handleClose}>
            <Box className="enterOTPModal">
                <DialogTitle className="enterOTPModalTitle">
                    Enter the OTP code
                    <br />
                    Project:{" "}
                    <i style={{ color: "green" }}>
                        {selectedProject?.name || "Unknown project"}
                    </i>
                    <Box className="enterOTPModalTimer">
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 1,
                                color:
                                    timeLeft <= 30
                                        ? "#d32f2f"
                                        : "text.secondary",
                                fontWeight: timeLeft <= 30 ? "bold" : "normal",
                            }}
                        >
                            Time remaining: {formatTime(timeLeft)}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent className="enterOTPModalContent">
                    <Typography
                        className="enterOTPModalContentText"
                        variant="body1"
                    >
                        To continue generating or running tests that require
                        login session, please add OTP code from email or SMS.
                    </Typography>
                    <TextField
                        label="OTP"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={otp}
                        onChange={handleOTPChange}
                        required
                        autoComplete="new-otp"
                        error={!!otpError}
                        helperText={otpError + " "}
                        disabled={timeLeft === 0}
                    />
                    <Alert className="enterOTPModalAlert" severity="info">
                        <AlertTitle className="enterOTPModalAlertTitle">
                            Important
                        </AlertTitle>
                        <span>
                            if session will expire, you&apos;ll be asked to
                            re-run login process and provide a new OTP code.
                            Also you can do it manually from login settings
                            page.
                        </span>
                    </Alert>
                </DialogContent>
                <DialogActions className="enterOTPModalActions">
                    <Button
                        className="enterOTPModalButton"
                        onClick={handleClose}
                        color="secondary"
                        variant="outlined"
                    >
                        CANCEL
                    </Button>
                    {enterOTPLoading ? (
                        <LoadingButton
                            loading
                            className="enterOTPModalButton"
                            variant="contained"
                            loadingIndicator={
                                <CircularProgress color="white" size={16} />
                            }
                        />
                    ) : (
                        <Button
                            className="enterOTPModalButton"
                            onClick={handleEnterOTP}
                            color="secondary"
                            variant="contained"
                            disabled={timeLeft === 0}
                        >
                            CONTINUE
                        </Button>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default EnterOTPModal;
